/**
 * Enterprise Roles Configuration
 * Defines role hierarchy, titles, permissions and UI config for Villarreal Academy
 */

// Role identifiers (stored in DB)
export const ROLE_KEYS = {
  SUPERADMIN: 'superadmin',
  ADMIN_ACADEMY: 'admin_academy',
  COACH: 'coach',
};

// Display titles - EXACT names to show in UI
export const ROLE_TITLES = {
  [ROLE_KEYS.SUPERADMIN]: 'Superadmin Villarreal Academy',
  [ROLE_KEYS.ADMIN_ACADEMY]: 'Admin de Academia',
  [ROLE_KEYS.COACH]: 'Entrenador',
};

// Short titles for badges and compact displays
export const ROLE_TITLES_SHORT = {
  [ROLE_KEYS.SUPERADMIN]: 'Superadmin',
  [ROLE_KEYS.ADMIN_ACADEMY]: 'Admin Academia',
  [ROLE_KEYS.COACH]: 'Entrenador',
};

// Role colors for UI consistency
export const ROLE_COLORS = {
  [ROLE_KEYS.SUPERADMIN]: {
    primary: '#FFD700',
    bg: 'rgba(255,215,0,0.12)',
    border: 'rgba(255,215,0,0.25)',
  },
  [ROLE_KEYS.ADMIN_ACADEMY]: {
    primary: '#4dd6ff',
    bg: 'rgba(77,214,255,0.12)',
    border: 'rgba(77,214,255,0.25)',
  },
  [ROLE_KEYS.COACH]: {
    primary: 'var(--primary)',
    bg: 'rgba(var(--primary-rgb),0.12)',
    border: 'rgba(var(--primary-rgb),0.25)',
  },
};

// Default home route per role
export const ROLE_HOME_ROUTES = {
  [ROLE_KEYS.SUPERADMIN]: '/enterprise/superadmin',
  [ROLE_KEYS.ADMIN_ACADEMY]: '/enterprise/academy', // Will append /{academy_id}
  [ROLE_KEYS.COACH]: '/dashboard',
};

// Role hierarchy for permission checks
export const ROLE_HIERARCHY = {
  [ROLE_KEYS.SUPERADMIN]: 3,
  [ROLE_KEYS.ADMIN_ACADEMY]: 2,
  [ROLE_KEYS.COACH]: 1,
};

/**
 * Get the effective enterprise role from user object
 * @param {object} user - User object
 * @param {boolean} inEnterpriseContext - Whether we're in an enterprise tenant context
 */
export function getEnterpriseRole(user, inEnterpriseContext = false) {
  if (!user) return null;
  
  // Only map admin to superadmin when in enterprise tenant context
  // In regular MiCoach context, admin should behave like a coach with extra admin panel
  if (user.role === 'admin' && inEnterpriseContext) {
    return ROLE_KEYS.SUPERADMIN;
  }
  
  // Enterprise role takes precedence (if user has explicit enterprise_role)
  if (user.enterprise_role) return user.enterprise_role;
  
  // Default to coach for regular users (including admin in non-enterprise context)
  return ROLE_KEYS.COACH;
}

/**
 * Get display title for a role
 */
export function getRoleTitle(roleKey, short = false) {
  const titles = short ? ROLE_TITLES_SHORT : ROLE_TITLES;
  return titles[roleKey] || 'Usuario';
}

/**
 * Get role color config
 */
export function getRoleColor(roleKey) {
  return ROLE_COLORS[roleKey] || ROLE_COLORS[ROLE_KEYS.COACH];
}

/**
 * Get home route for a user based on their role
 * @param {object} user - User object
 * @param {boolean} inEnterpriseContext - Whether we're in an enterprise tenant context
 */
export function getHomeRoute(user, inEnterpriseContext = false) {
  const role = getEnterpriseRole(user, inEnterpriseContext);
  
  if (role === ROLE_KEYS.SUPERADMIN && inEnterpriseContext) {
    return ROLE_HOME_ROUTES[ROLE_KEYS.SUPERADMIN];
  }
  
  if (role === ROLE_KEYS.ADMIN_ACADEMY && inEnterpriseContext) {
    const academyId = user.academy_id || '';
    return `${ROLE_HOME_ROUTES[ROLE_KEYS.ADMIN_ACADEMY]}/${academyId}`;
  }
  
  return ROLE_HOME_ROUTES[ROLE_KEYS.COACH];
}

/**
 * Check if user has at least the given role level
 * @param {boolean} inEnterpriseContext - Whether we're in an enterprise tenant context
 */
export function hasRoleLevel(user, requiredRole, inEnterpriseContext = false) {
  const userRole = getEnterpriseRole(user, inEnterpriseContext);
  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
  return userLevel >= requiredLevel;
}

/**
 * Check if user is superadmin (only in enterprise context)
 * @param {boolean} inEnterpriseContext - Whether we're in an enterprise tenant context
 */
export function isSuperAdmin(user, inEnterpriseContext = false) {
  return getEnterpriseRole(user, inEnterpriseContext) === ROLE_KEYS.SUPERADMIN;
}

/**
 * Check if user is admin of academy
 * @param {boolean} inEnterpriseContext - Whether we're in an enterprise tenant context
 */
export function isAcademyAdmin(user, inEnterpriseContext = false) {
  const role = getEnterpriseRole(user, inEnterpriseContext);
  return role === ROLE_KEYS.ADMIN_ACADEMY || role === ROLE_KEYS.SUPERADMIN;
}

/**
 * Check if user should see enterprise navigation
 * Only true when in enterprise tenant context AND user has enterprise role
 * @param {boolean} inEnterpriseContext - Whether we're in an enterprise tenant context
 */
export function shouldShowEnterpriseNav(user, inEnterpriseContext = false) {
  if (!inEnterpriseContext) return false;
  const role = getEnterpriseRole(user, inEnterpriseContext);
  return role === ROLE_KEYS.SUPERADMIN || role === ROLE_KEYS.ADMIN_ACADEMY;
}

/**
 * Check if user should see coach/training navigation
 * True for coaches, OR for any user NOT in enterprise admin context
 * @param {boolean} inEnterpriseContext - Whether we're in an enterprise tenant context
 */
export function shouldShowCoachNav(user, inEnterpriseContext = false) {
  const role = getEnterpriseRole(user, inEnterpriseContext);
  // Show coach nav for coaches, OR for admins when NOT in enterprise context
  if (role === ROLE_KEYS.COACH) return true;
  // MiCoach admin (role='admin') sees coach nav when NOT in enterprise context
  if (!inEnterpriseContext && user?.role === 'admin') return true;
  return false;
}

/**
 * Roles available for user creation (based on creator's role)
 * @param {boolean} inEnterpriseContext - Whether we're in an enterprise tenant context
 */
export function getCreatableRoles(creatorUser, inEnterpriseContext = false) {
  const role = getEnterpriseRole(creatorUser, inEnterpriseContext);
  
  if (role === ROLE_KEYS.SUPERADMIN) {
    return [
      { value: ROLE_KEYS.COACH, label: ROLE_TITLES[ROLE_KEYS.COACH] },
      { value: ROLE_KEYS.ADMIN_ACADEMY, label: ROLE_TITLES[ROLE_KEYS.ADMIN_ACADEMY] },
    ];
  }
  
  if (role === ROLE_KEYS.ADMIN_ACADEMY) {
    return [
      { value: ROLE_KEYS.COACH, label: ROLE_TITLES[ROLE_KEYS.COACH] },
    ];
  }
  
  return [];
}

export default {
  ROLE_KEYS,
  ROLE_TITLES,
  ROLE_TITLES_SHORT,
  ROLE_COLORS,
  ROLE_HOME_ROUTES,
  getEnterpriseRole,
  getRoleTitle,
  getRoleColor,
  getHomeRoute,
  hasRoleLevel,
  isSuperAdmin,
  isAcademyAdmin,
  shouldShowEnterpriseNav,
  shouldShowCoachNav,
  getCreatableRoles,
};
